import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Toast suppression around 402 rejections ─────────────────────────────────
// Many callers pattern `catch { toast.error('Erreur…'); }` without inspecting
// the error object. Since 402 already shows the UpgradePrompt modal, a noisy
// red toast right after would be duplicate and confusing. We patch `toast.error`
// to silently drop any error toast fired within 500ms of a 402 rejection.
let _last402 = 0;
const _origToastError = toast.error.bind(toast);
toast.error = (msg, ...rest) => {
  if (Date.now() - _last402 < 500) return;
  return _origToastError(msg, ...rest);
};

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Imperative handle for UpgradePrompt — registered by UpgradePromptProvider on mount.
// Allows the axios interceptor to trigger the upgrade modal without React context.
let _showUpgrade = null;
export function registerUpgradeHandler(fn) {
  _showUpgrade = fn;
}

// Handle 401 + 402 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (status === 402) {
      const detail = error.response?.data?.detail ?? {};
      const triggerId = detail.trigger_id;

      // Always surface the UpgradePrompt modal
      if (triggerId && _showUpgrade) {
        _showUpgrade(triggerId, {
          current_usage: detail.current_usage,
          limit: detail.limit,
        });
      }

      // Debug trace — visible in browser console, easy to spot in prod logs
      // eslint-disable-next-line no-console
      console.warn(`Creation blocked by plan limit: ${triggerId}`, detail);

      // Reject with a sentinel so `await` throws → callers' toast.success
      // is skipped. Any toast.error fired within 500ms is also suppressed
      // via the _last402 timestamp check on toast.error above.
      _last402 = Date.now();
      error.__limitReached = true;
      error.__trigger = triggerId;
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export const dashboardApi = {
  getSummary: () => api.get('/dashboard/summary'),
  getSparklines: () => api.get('/dashboard/sparklines'),
};

export const authApi = {
  updateProfile: (data) => api.put('/auth/profile', data),
  sendVerification: (data) => api.post('/auth/register', data),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  verifyEmail: (email, code) => api.post('/auth/verify-email', { email, code }),
};

export const stocksApi = {
  getAll: () => api.get('/stocks'),
  create: (data) => api.post('/stocks', data),
  update: (id, data) => api.put(`/stocks/${id}`, data),
  delete: (id) => api.delete(`/stocks/${id}`)
};

export const realEstateApi = {
  getAll: () => api.get('/real-estate'),
  create: (data) => api.post('/real-estate', data),
  update: (id, data) => api.put(`/real-estate/${id}`, data),
  delete: (id) => api.delete(`/real-estate/${id}`)
};

export const cryptoApi = {
  getAll: () => api.get('/crypto'),
  create: (data) => api.post('/crypto', data),
  update: (id, data) => api.put(`/crypto/${id}`, data),
  delete: (id) => api.delete(`/crypto/${id}`)
};

export const bondsApi = {
  getAll: () => api.get('/bonds'),
  create: (data) => api.post('/bonds', data),
  update: (id, data) => api.put(`/bonds/${id}`, data),
  delete: (id) => api.delete(`/bonds/${id}`)
};

export const expensesApi = {
  getAll: () => api.get('/expenses'),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`)
};

export const loansApi = {
  getAll: () => api.get('/loans'),
  create: (data) => api.post('/loans', data),
  update: (id, data) => api.put(`/loans/${id}`, data),
  delete: (id) => api.delete(`/loans/${id}`)
};

export const goalsApi = {
  getAll: () => api.get('/goals'),
  create: (data) => api.post('/goals', data),
  update: (id, data) => api.put(`/goals/${id}`, data),
  delete: (id) => api.delete(`/goals/${id}`)
};

export const collectionApi = {
  getAll: () => api.get('/collection'),
  create: (data) => api.post('/collection', data),
  update: (id, data) => api.put(`/collection/${id}`, data),
  delete: (id) => api.delete(`/collection/${id}`)
};

export const transactionsApi = {
  getAll: (category, assetId) => api.get(`/transactions/${category}/${assetId}`),
  getByCategory: (category) => api.get(`/transactions/${category}`),
  create: (data) => api.post('/transactions', data),
  update: (txId, data) => api.put(`/transactions/${txId}`, data),
  delete: (txId) => api.delete(`/transactions/${txId}`)
};

export const liquiditesApi = {
  getAll: () => api.get('/liquidites'),
  create: (data) => api.post('/liquidites', data),
  update: (id, data) => api.put(`/liquidites/${id}`, data),
  delete: (id) => api.delete(`/liquidites/${id}`)
};

export const revenusApi = {
  getAll: () => api.get('/income'),
  create: (data) => api.post('/income', data),
  update: (id, data) => api.put(`/income/${id}`, data),
  delete: (id) => api.delete(`/income/${id}`)
};

export const epargneApi = {
  getAll: () => api.get('/savings'),
  create: (data) => api.post('/savings', data),
  update: (id, data) => api.put(`/savings/${id}`, data),
  delete: (id) => api.delete(`/savings/${id}`),
  getTransactions: (id) => api.get(`/savings/${id}/transactions`),
  addTransaction: (id, data) => api.post(`/savings/${id}/transactions`, data),
  deleteTransaction: (savingsId, txId) => api.delete(`/savings/${savingsId}/transactions/${txId}`)
};

export const wishlistApi = {
  getAll: () => api.get('/wishlist'),
  add: (data) => api.post('/wishlist', data),
  remove: (id) => api.delete(`/wishlist/${id}`)
};

export const marketApi = {
  getCryptoPrice: (coinId) => api.get(`/market/crypto/${coinId}`),
  getCryptoTop200: () => api.get('/market/crypto-top200'),
  getStockPrice: (symbol) => api.get(`/market/stock/${symbol}`),
  getAllStocks: () => api.get('/market/stocks'),
  getAllCryptos: () => api.get('/market/cryptos'),
  getAllEtfs: () => api.get('/market/etfs'),
  getScreener: () => api.get('/market/screener'),
  getScreenerFull: () => api.get('/market/screener/full'),
  getBatchPrices: (symbols) => api.get(`/market/prices?symbols=${symbols.join(',')}`),
  getDividendYield: (symbol) => api.get(`/market/stock/${symbol}/dividend`),
  getBatchDividends: (symbols) => api.get(`/market/stocks/dividends?symbols=${symbols.join(',')}`),
  searchStocks: (q) => api.get(`/market/search?q=${encodeURIComponent(q)}`),
  getMetals: () => api.get('/market/metals'),
};

export const subscriptionApi = {
  buyCategory: (originUrl, category) => api.post('/subscription/buy-category', { origin_url: originUrl, category }),
  getStatus: (sessionId) => api.get(`/subscription/status/${sessionId}`),
  getInfo: () => api.get('/subscription/info'),
  getMe: () => api.get('/subscription/me'),
  getBilling: () => api.get('/subscription/billing'),
  getPortalSession: () => api.post('/subscription/portal-session'),
  setActiveTemplate: (templateId) => api.put(`/users/me/active-template/${templateId}`),
};

export const checkoutApi = {
  createSession: (plan, source) => api.post('/checkout/session', { plan, source }),
  getConfig: () => api.get('/checkout/config'),
};

export const householdApi = {
  get: () => api.get('/household'),
};

export const idealTemplatesApi = {
  list:   () => api.get('/ideal-templates'),
  create: (data) => api.post('/ideal-templates', data),
  update: (id, data) => api.put(`/ideal-templates/${id}`, data),
  remove: (id) => api.delete(`/ideal-templates/${id}`),
};

export const feedbackApi = {
  send: (category, message) => api.post('/feedback', { category, message }),
};

export const simulatorApi = {
  simulateCredit: (data) => api.post('/simulator/credit', data)
};

export const familyApi = {
  getCode: () => api.get('/family/code'),
  createFamily: () => api.post('/family/create'),
  getFamily: () => api.get('/family'),
  getPatrimony: () => api.get('/family/patrimony'),
  requestJoin: (family_code) => api.post('/family/request', { family_code }),
  respondToInvite: (invite_id, action) => api.post('/family/invite/respond', { invite_id, action }),
  updateSettings: (shared_categories) => api.put('/family/settings', { shared_categories }),
  leaveFamily: () => api.post('/family/leave'),
  dissolveFamily: () => api.delete('/family'),
  removeMember: (user_id) => api.delete(`/family/members/${user_id}`),
  addManagedMember: (data) => api.post('/family/managed-members', data),
  removeManagedMember: (managed_id) => api.delete(`/family/managed-members/${managed_id}`),
  getManagedAssets: (managed_id) => api.get(`/family/managed-members/${managed_id}/assets`),
  addManagedAsset: (managed_id, data) => api.post(`/family/managed-members/${managed_id}/assets`, data),
  updateManagedAsset: (managed_id, asset_id, data) => api.put(`/family/managed-members/${managed_id}/assets/${asset_id}`, data),
  deleteManagedAsset: (managed_id, asset_id) => api.delete(`/family/managed-members/${managed_id}/assets/${asset_id}`)
};

export const familyCommonPatrimonyApi = {
  getSummary: () => api.get('/family/common-assets/summary'),
  getItems: (category) => api.get(`/family/common-assets?category=${category}`),
  getCategoryTransactions: (category) => api.get(`/family/common-assets/category-transactions?category=${category}`),
  create: (data) => api.post('/family/common-assets', data),
  update: (itemId, data) => api.put(`/family/common-assets/${itemId}`, data),
  delete: (itemId) => api.delete(`/family/common-assets/${itemId}`),
  getTransactions: (itemId) => api.get(`/family/common-assets/${itemId}/transactions`),
  addTransaction: (itemId, data) => api.post(`/family/common-assets/${itemId}/transactions`, data),
  deleteTransaction: (itemId, txId) => api.delete(`/family/common-assets/${itemId}/transactions/${txId}`)
};

export const familyBudgetApi = {
  getAll: (periodId) => api.get('/family/budget', { params: periodId ? { period_id: periodId } : {} }),
  add: (data) => api.post('/family/budget', data),
  update: (itemId, data) => api.put(`/family/budget/${itemId}`, data),
  remove: (itemId) => api.delete(`/family/budget/${itemId}`),
};

export const familyBudgetPeriodsApi = {
  getActive: () => api.get('/family/budget/periods/active'),
  getAll: () => api.get('/family/budget/periods'),
  create: (data) => api.post('/family/budget/periods', data),
  close: (periodId) => api.post(`/family/budget/periods/${periodId}/close`),
  remove: (periodId) => api.delete(`/family/budget/periods/${periodId}`),
};

export const familyBudgetCategoriesApi = {
  getAll: () => api.get('/family/budget/categories'),
  add: (data) => api.post('/family/budget/categories', data),
  remove: (categoryId) => api.delete(`/family/budget/categories/${categoryId}`),
};

export const familyTodoApi = {
  getAll: () => api.get('/family/todos'),
  add: (data) => api.post('/family/todos', data),
  toggle: (todoId) => api.patch(`/family/todos/${todoId}/toggle`),
  remove: (todoId) => api.delete(`/family/todos/${todoId}`),
};

export const documentsApi = {
  getFolders: (type) => api.get(`/documents/folders?type=${type}`),
  createFolder: (data, type) => api.post(`/documents/folders?type=${type}`, data),
  updateFolder: (id, data) => api.put(`/documents/folders/${id}`, data),
  deleteFolder: (id) => api.delete(`/documents/folders/${id}`),
  getDocuments: (folderId) => api.get(`/documents?folder_id=${folderId}`),
  previewDocument: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  deleteDocument: (id) => api.delete(`/documents/${id}`),
  uploadDocument: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getDownloadUrl: (docId) => `${API_URL}/api/documents/${docId}/download`,
  getPdfs: () => api.get('/documents/pdfs'),
};

export const personalBudgetApi = {
  createPeriod: (data) => api.post('/personal-budget/periods', data),
  listPeriods: () => api.get('/personal-budget/periods'),
  updatePeriod: (id, data) => api.put(`/personal-budget/periods/${id}`, data),
  closePeriod: (id, data) => api.post(`/personal-budget/periods/${id}/close`, data),
  reopenPeriod: (id) => api.post(`/personal-budget/periods/${id}/reopen`),
  deletePeriod: (id) => api.delete(`/personal-budget/periods/${id}`),
  getCurrentStatus: () => api.get('/budgets/current-status'),
};

export const compassSubscriptionApi = {
  getMe: () => api.get('/subscription/me'),
  getUsage: () => api.get('/subscription/usage'),
};

// Replaces the legacy `patrimony_account_pending_v1` localStorage key.
// Composite key on the backend: (user_id, period_key, category, account_id).
// POST is idempotent — safe to call repeatedly during the silent migration.
// `sync` replaces the user's full entry set in one round-trip (used by
// the auto-sync façade to mirror localStorage writes after every change).
export const accountPendingApi = {
  list:    () => api.get('/budget/account-pending'),
  upsert:  (entry) => api.post('/budget/account-pending', entry),
  remove:  (periodKey, category, accountId) =>
    api.delete(`/budget/account-pending/${encodeURIComponent(periodKey)}/${encodeURIComponent(category)}/${encodeURIComponent(accountId)}`),
  sync:    (nestedMap) => api.put('/budget/account-pending/sync', nestedMap),
};

// Replaces `patrimony_monthly_checks_v1` (personal) and
// `patrimony_family_monthly_checks_v1` (family). Scope is mandatory.
export const monthlyChecksApi = {
  list:   (scope = 'personal') => api.get(`/patrimony/monthly-checks?scope=${scope}`),
  set:    (assetId, month, scope = 'personal') =>
    api.post('/patrimony/monthly-checks', { asset_id: assetId, month, scope }),
  remove: (assetId, month, scope = 'personal') =>
    api.delete(`/patrimony/monthly-checks/${scope}/${encodeURIComponent(assetId)}/${encodeURIComponent(month)}`),
};

// Generic blob storage for the localStorage keys migrated in PR2 of the
// localStorage→backend migration plan (budget allocations, family allocs,
// custom categories, family goals, simulator history, wallet configs, …).
// PUT replaces the full blob (no per-row diffing). GET returns every key
// for the user as a {key: value} map in one round-trip.
export const userSettingsApi = {
  getAll: ()         => api.get('/user-settings'),
  set:    (key, val) => api.put(`/user-settings/${encodeURIComponent(key)}`, { value: val }),
  remove: (key)      => api.delete(`/user-settings/${encodeURIComponent(key)}`),
};

// RGPD self-service endpoints (Articles 15, 17, 20).
//   • exportData()  → triggers a ZIP download of all user data
//   • deleteAccount(password) → cascades the user deletion across every
//                               collection, password-confirmed
export const rgpdApi = {
  exportData:    () => api.get('/users/me/export-data', { responseType: 'blob' }),
  deleteAccount: (password) => api.delete('/users/me', { data: { password } }),
};

// Wallet (groupements actions/crypto) deletion. Three actions:
//  • { action: 'move_to_other', target_wallet_name } — reassigns assets
//  • { action: 'set_orphan' }                       — assets → 'Principal'
//  • { action: 'delete_all' }                       — assets deleted too
export const walletsApi = {
  remove: (kind, walletName, body) =>
    api.delete(
      `/patrimony/wallets/${kind}/${encodeURIComponent(walletName)}`,
      { data: body }
    ),
};

export default api;
